/**
 * Customer portal (Wave 5C): balance, CDRs, invoices + Stripe top-up.
 * Authenticates with the customer JWT issued by /auth/reseller/login
 * (role=customer); consumes the /api/v1/client/* API.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import apiClient, { clearResellerToken } from "../../api/client";
import TopUpDialog from "../../components/customer/TopUpDialog";

const money = (v: number) => `$${(v ?? 0).toFixed(4)}`;

export default function CustomerPortal() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [balance, setBalance] = useState<number | null>(null);
  const [account, setAccount] = useState("");
  const [cdrs, setCdrs] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [topupMsg, setTopupMsg] = useState("");
  const [topupOpen, setTopupOpen] = useState(false);

  const logout = () => {
    clearResellerToken();
    navigate("/customer/login");
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Cookie-first auth: httpOnly reseller_token cookie rides along via
      // the shared client's withCredentials; no Bearer header needed.
      const [me, bal, cdrsResp, invResp] = await Promise.all([
        apiClient.get("/api/v1/client/me"),
        apiClient.get("/api/v1/client/balance"),
        apiClient.get("/api/v1/client/cdrs?limit=100"),
        apiClient.get("/api/v1/client/invoices").catch(() => ({ data: { invoices: [] } })),
      ]);
      setAccount(me.data.account);
      setBalance(bal.data.balance);
      setCdrs(cdrsResp.data.cdrs || []);
      setInvoices(invResp.data.invoices || []);
    } catch (err: any) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 401) {
        logout();
        return;
      }
      setError(detail || "Failed to load account data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPdf = async (invoiceNumber: string) => {
    try {
      const resp = await fetch(`/api/v1/client/invoices/${encodeURIComponent(invoiceNumber)}/pdf`);
      if (!resp.ok) throw new Error("PDF download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || "PDF download failed");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  /** C1: create a Stripe payment session via the TopUpDialog (no window.prompt). */
  const createTopupIntent = useCallback(async (value: number) => {
    const resp = await apiClient.post(
      `/api/v1/client/topup/create?amount=${value}`
    );
    setTopupMsg(
      `Payment session created (${resp.data.payment_intent_id}). Complete payment to add ${money(value)}.`
    );
    return String(resp.data.payment_intent_id ?? "");
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">My Account</Typography>
        <Button variant="outlined" onClick={logout}>Log out</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Account</Typography>
            <Typography variant="h6" sx={{ fontFamily: "monospace", mb: 2 }}>{account}</Typography>
            <Typography variant="body2" color="text.secondary">Balance</Typography>
            <Typography variant="h3">{balance != null ? money(balance) : "—"}</Typography>
            <Button
              variant="contained"
              startIcon={<AccountBalanceWalletIcon />}
              onClick={() => setTopupOpen(true)}
              sx={{ mt: 2 }}
            >
              Top up
            </Button>
            {topupMsg && (
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {topupMsg}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              API access: use your API key against /api/v1/client/* (see docs/client-api.md)
            </Typography>
          </CardContent>
        </Card>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
          <Tab label="Call history" />
          <Tab label="Invoices" />
        </Tabs>
        <TableContainer>
          {tab === 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell align="right">Duration (s)</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell>Hangup</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cdrs.map((c) => (
                  <TableRow key={c.call_id} hover>
                    <TableCell>{c.start_time ? new Date(c.start_time).toLocaleString() : "—"}</TableCell>
                    <TableCell>{c.caller}</TableCell>
                    <TableCell>{c.callee}</TableCell>
                    <TableCell align="right">{c.billsec}</TableCell>
                    <TableCell align="right">{money(c.cost)}</TableCell>
                    <TableCell>{c.hangup_cause}</TableCell>
                  </TableRow>
                ))}
                {cdrs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">No calls yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          {tab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.invoice_number} hover>
                    <TableCell>{inv.invoice_number}</TableCell>
                    <TableCell>
                      {new Date(inv.period_start).toLocaleDateString()} –{" "}
                      {new Date(inv.period_end).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">{money(inv.total_amount)}</TableCell>
                    <TableCell><Chip label={inv.status} size="small" /></TableCell>
                    <TableCell>{new Date(inv.due_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => downloadPdf(inv.invoice_number)}
                      >
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">No invoices yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* C1: proper top-up dialog instead of window.prompt */}
      <TopUpDialog
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        onCreateIntent={createTopupIntent}
      />
    </Box>
  );
}